import { createSlice } from "@reduxjs/toolkit";
import {
  createLead,
  getAllLeads,
  getLeadById,
  updateLead,
  deleteLead,
  updateLeadStatus,
  getLeadStats,
  getPieChartLeads,
  getLeadsConversion,
} from "./leadAction";

interface LeadState {
  leads: {
    _id: string;
    fullname: string;
    email: string;
    companyName: string;
    phoneNumber: string;
    teamSize: string;
    description: string;
    userId: string;
    status?: string;
    createdAt: string;
    updatedAt: string;
  }[];
  selectedLead: {
    _id: string;
    fullname: string;
    email: string;
    companyName: string;
    phoneNumber: string;
    teamSize: string;
    description: string;
    userId: string;
    status?: string;
    createdAt: string;
    updatedAt: string;
  } | null;
  leadStats: {
    totalLeads: number;
    newLeads: number;
    conversionRate: string;
    churnRate: string;
    followUps: number;
  } | null;
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalLeads: number;
    totalPages: number;
  } | null;

  leadStatLoading: boolean;
  leadStatError: string | null;

  PieChartLeads: {
    _id: string;
    fullname: string;
    email: string;
    companyName: string;
    phoneNumber: string;
    teamSize: string;
    description: string;
    userId: string;
    status?: string;
    createdAt: string;
    updatedAt: string;
  }[];
  PieChartLeadloading: boolean;
  PieChartLeadError: string | null;
  PieChartLeadPagination: {
    currentPage: number;
    pageSize: number;
    totalLeads: number;
    totalPages: number;
  } | null;

  leadsConversion: {
    year: number;
    stats: {
      month: string;
      totalLeads: number;
      totalConversions: number;
    }[];
  } | null;
  leadsConversionLoading: boolean;
  leadsConversionError: string | null;
}

const initialState: LeadState = {
  leads: [],
  selectedLead: null,
  leadStats: null,
  loading: false,
  error: null,
  pagination: null,

  leadStatLoading: false,
  leadStatError: null,

  PieChartLeads: [],
  PieChartLeadloading: false,
  PieChartLeadError: null,
  PieChartLeadPagination: null,

  leadsConversion: null,
  leadsConversionLoading: false,
  leadsConversionError: null,
};

const leadSlice = createSlice({
  name: "lead",
  initialState,
  reducers: {
    clearLeadMessages(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // CREATE LEAD
      .addCase(createLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createLead.fulfilled, (state, action) => {
        state.loading = false;
        const lead = action.payload.data;
        if (lead) {
          state.leads.push(lead);
        }
        state.error = null;
      })
      .addCase(createLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create lead";
      })

      // GET ALL LEADS
      .addCase(getAllLeads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllLeads.fulfilled, (state, action) => {
        state.loading = false;
        state.leads = action.payload.data?.leads || [];
        state.pagination = action.payload.data?.pagination || null;
        state.error = null;
      })
      .addCase(getAllLeads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to retrieve leads";
      })

      // GET ALL LEADS BY STATUS
      .addCase(getPieChartLeads.pending, (state) => {
        state.PieChartLeadloading = true;
        state.PieChartLeadError = null;
      })
      .addCase(getPieChartLeads.fulfilled, (state, action) => {
        state.PieChartLeadloading = false;
        state.PieChartLeads = action.payload.data?.leads || [];
        state.PieChartLeadPagination = action.payload.data?.pagination || null;
        state.PieChartLeadError = null;
      })
      .addCase(getPieChartLeads.rejected, (state, action) => {
        state.PieChartLeadloading = false;
        state.PieChartLeadError = action.payload || "Failed to retrieve leads";
      })

      // GET LEAD BY ID
      .addCase(getLeadById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getLeadById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedLead = action.payload.data || null;
        state.error = null;
      })
      .addCase(getLeadById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to retrieve lead";
      })

      // UPDATE LEAD
      .addCase(updateLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLead.fulfilled, (state, action) => {
        state.loading = false;
        const updatedLead = action.payload.data;
        if (updatedLead) {
          state.leads = state.leads.map((lead) =>
            lead._id === updatedLead._id ? updatedLead : lead
          );
          if (state.selectedLead?._id === updatedLead._id) {
            state.selectedLead = updatedLead;
          }
        }
        state.error = null;
      })
      .addCase(updateLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update lead";
      })

      // UPDATE LEAD STATUS
      .addCase(updateLeadStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLeadStatus.fulfilled, (state, action) => {
        state.loading = false;
        const updatedLead = action.payload.data;
        if (updatedLead) {
          state.leads = state.leads.map((lead) =>
            lead._id === updatedLead._id ? updatedLead : lead
          );
          if (state.selectedLead?._id === updatedLead._id) {
            state.selectedLead = updatedLead;
          }
        }
        state.error = null;
      })
      .addCase(updateLeadStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update lead status";
      })

      // DELETE LEAD
      .addCase(deleteLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteLead.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.meta.arg;
        state.leads = state.leads.filter((lead) => lead._id !== deletedId);
        if (state.selectedLead?._id === deletedId) {
          state.selectedLead = null;
        }
        state.error = null;
      })
      .addCase(deleteLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete lead";
      })

      // GET LEAD STATS
      .addCase(getLeadStats.pending, (state) => {
        state.leadStatLoading = true;
        state.leadStatError = null;
      })
      .addCase(getLeadStats.fulfilled, (state, action) => {
        state.leadStatLoading = false;
        state.leadStats = action.payload.data || null;
        state.leadStatError = null;
      })
      .addCase(getLeadStats.rejected, (state, action) => {
        state.leadStatLoading = false;
        state.leadStatError =
          action.payload || "Failed to retrieve lead statistics";
      })

      // GET LEADS CONVERSION
      .addCase(getLeadsConversion.pending, (state) => {
        state.leadsConversionLoading = true;
        state.leadsConversionError = null;
      })
      .addCase(getLeadsConversion.fulfilled, (state, action) => {
        state.leadsConversionLoading = false;
        state.leadsConversion = action.payload.data || null;
        state.leadsConversionError = null;
      })
      .addCase(getLeadsConversion.rejected, (state, action) => {
        state.leadsConversionLoading = false;
        state.leadsConversionError =
          action.payload || "Failed to retrieve leads conversion";
      });
  },
});

export const { clearLeadMessages } = leadSlice.actions;
export default leadSlice.reducer;
